export enum SessionStatus {
    INITIALIZING = 'initializing',
    AUTHENTICATING = 'authenticating',
    AUTHENTICATED = 'authenticated',
    REVALIDATING = 'revalidating',
    LOGGING_OUT = 'logging_out',
    EXPIRED = 'expired',
    UNAUTHENTICATED = 'unauthenticated',
}

export enum SessionEvent {
    SESSION_CHECK_STARTED = 'session_check_started',
    LOGIN_STARTED = 'login_started',
    AUTHENTICATION_CONFIRMED = 'authentication_confirmed',
    AUTHENTICATION_FAILED = 'authentication_failed',
    REVALIDATION_FAILED = 'revalidation_failed',
    LOGOUT_STARTED = 'logout_started',
    LOGOUT_COMPLETED = 'logout_completed',
    LOGOUT_FAILED = 'logout_failed',
    SESSION_EXPIRED = 'session_expired',
}

type SessionTransitionMap = Record<SessionStatus, Partial<Record<SessionEvent, SessionStatus>>>;

const sessionTransitions: SessionTransitionMap = {
    [SessionStatus.INITIALIZING]: {
        [SessionEvent.SESSION_CHECK_STARTED]: SessionStatus.INITIALIZING,
        [SessionEvent.LOGIN_STARTED]: SessionStatus.AUTHENTICATING,
        [SessionEvent.AUTHENTICATION_CONFIRMED]: SessionStatus.AUTHENTICATED,
        [SessionEvent.AUTHENTICATION_FAILED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.LOGOUT_STARTED]: SessionStatus.LOGGING_OUT,
        [SessionEvent.LOGOUT_COMPLETED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.LOGOUT_FAILED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.SESSION_EXPIRED]: SessionStatus.UNAUTHENTICATED,
    },
    [SessionStatus.AUTHENTICATING]: {
        [SessionEvent.AUTHENTICATION_CONFIRMED]: SessionStatus.AUTHENTICATED,
        [SessionEvent.AUTHENTICATION_FAILED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.LOGOUT_STARTED]: SessionStatus.LOGGING_OUT,
        [SessionEvent.LOGOUT_COMPLETED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.SESSION_EXPIRED]: SessionStatus.EXPIRED,
    },
    [SessionStatus.AUTHENTICATED]: {
        [SessionEvent.SESSION_CHECK_STARTED]: SessionStatus.REVALIDATING,
        [SessionEvent.LOGIN_STARTED]: SessionStatus.AUTHENTICATING,
        [SessionEvent.AUTHENTICATION_CONFIRMED]: SessionStatus.AUTHENTICATED,
        [SessionEvent.AUTHENTICATION_FAILED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.LOGOUT_STARTED]: SessionStatus.LOGGING_OUT,
        [SessionEvent.LOGOUT_COMPLETED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.SESSION_EXPIRED]: SessionStatus.EXPIRED,
    },
    [SessionStatus.REVALIDATING]: {
        [SessionEvent.SESSION_CHECK_STARTED]: SessionStatus.REVALIDATING,
        [SessionEvent.AUTHENTICATION_CONFIRMED]: SessionStatus.AUTHENTICATED,
        [SessionEvent.AUTHENTICATION_FAILED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.REVALIDATION_FAILED]: SessionStatus.AUTHENTICATED,
        [SessionEvent.LOGOUT_STARTED]: SessionStatus.LOGGING_OUT,
        [SessionEvent.LOGOUT_COMPLETED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.SESSION_EXPIRED]: SessionStatus.EXPIRED,
    },
    [SessionStatus.LOGGING_OUT]: {
        [SessionEvent.LOGOUT_COMPLETED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.LOGOUT_FAILED]: SessionStatus.UNAUTHENTICATED,
    },
    [SessionStatus.EXPIRED]: {
        [SessionEvent.SESSION_CHECK_STARTED]: SessionStatus.INITIALIZING,
        [SessionEvent.LOGIN_STARTED]: SessionStatus.AUTHENTICATING,
        [SessionEvent.LOGOUT_STARTED]: SessionStatus.LOGGING_OUT,
        [SessionEvent.LOGOUT_COMPLETED]: SessionStatus.UNAUTHENTICATED,
        [SessionEvent.SESSION_EXPIRED]: SessionStatus.EXPIRED,
    },
    [SessionStatus.UNAUTHENTICATED]: {
        [SessionEvent.SESSION_CHECK_STARTED]: SessionStatus.INITIALIZING,
        [SessionEvent.LOGIN_STARTED]: SessionStatus.AUTHENTICATING,
        [SessionEvent.LOGOUT_STARTED]: SessionStatus.LOGGING_OUT,
        [SessionEvent.LOGOUT_COMPLETED]: SessionStatus.UNAUTHENTICATED,
    },
};

export const transitionSession = (
    currentStatus: SessionStatus,
    event: SessionEvent,
): SessionStatus => sessionTransitions[currentStatus][event] || currentStatus;

export const isAuthenticatedSession = (status: SessionStatus): boolean =>
    status === SessionStatus.AUTHENTICATED || status === SessionStatus.REVALIDATING;

export const isSignedOutSession = (status: SessionStatus): boolean =>
    status === SessionStatus.UNAUTHENTICATED || status === SessionStatus.EXPIRED;

export const canAcceptAuthenticationResult = (status: SessionStatus): boolean =>
    status === SessionStatus.INITIALIZING ||
    status === SessionStatus.AUTHENTICATING ||
    status === SessionStatus.AUTHENTICATED ||
    status === SessionStatus.REVALIDATING;
